/* ==========================================================================
   FLEX BLOCK — Shrine Pro 1.4.3
   --------------------------------------------------------------------------
   The block is authored inside the product info column (so it can be dragged
   to any position for MOBILE). On desktop (>= 990px) it moves itself to the
   position chosen in the block settings, and moves back on mobile.

   Note: main-product.liquid renders a second .product__media-wrapper when
   "Media position" is set to Right, so the visible column is picked at runtime.

   File: assets/product-flex-block.js
   ========================================================================== */

(function () {
  if (!window.customElements || customElements.get('product-flex-block')) return;

  var DESKTOP_QUERY = '(min-width: 990px)';

  /* Selectors are tried in order — the first visible match becomes the target. */
  var MEDIA_SELECTORS = [
    '.product__media-wrapper',
    '.product__media-column',
    '.product-media-container',
    '.product__media-gallery',
    'media-gallery'
  ];

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  class ProductFlexBlock extends HTMLElement {
    constructor() {
      super();
      this.placement = this.getAttribute('data-desktop-placement') || 'in_info';
      this.mql = window.matchMedia(DESKTOP_QUERY);
      this.onBreakpointChange = this.place.bind(this);
      this.listening = false;
      this.moved = false;
    }

    connectedCallback() {
      if (this.placement === 'in_info') {
        this.setAttribute('data-placed', 'in_info');
        return;
      }

      this.removeStaleCopies();

      /* Remember where the block lives in the info column before moving it. */
      if (!this.moved) {
        if (!this.anchor || !this.anchor.parentNode) {
          this.anchor = document.createComment('product-flex-block');
          this.parentNode.insertBefore(this.anchor, this);
        }

        if (!this.productEl || !this.productEl.isConnected) {
          var info = this.closest('.product__info-wrapper');
          this.productEl = this.closest('.product') || (info ? info.parentElement : null);
        }
      }

      this.addBreakpointListener();

      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.place.bind(this));
    }

    disconnectedCallback() {
      /* Moving a node fires disconnect + connect, so only clean up for real. */
      if (this.cleanupTimer) clearTimeout(this.cleanupTimer);
      this.cleanupTimer = setTimeout(
        function () {
          if (!this.isConnected) this.removeBreakpointListener();
        }.bind(this),
        0
      );
    }

    addBreakpointListener() {
      if (this.listening) return;
      if (this.mql.addEventListener) this.mql.addEventListener('change', this.onBreakpointChange);
      else this.mql.addListener(this.onBreakpointChange);
      this.listening = true;
    }

    removeBreakpointListener() {
      if (!this.listening) return;
      if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.onBreakpointChange);
      else this.mql.removeListener(this.onBreakpointChange);
      this.listening = false;
    }

    /* If the theme re-renders the info column, drop any orphaned copy. */
    removeStaleCopies() {
      var id = this.getAttribute('data-block-id');
      var scope = this.closest('.shopify-section');
      if (!id || !scope) return;

      var copies = scope.querySelectorAll('product-flex-block[data-block-id="' + id + '"]');
      for (var i = 0; i < copies.length; i++) {
        if (copies[i] !== this) copies[i].remove();
      }
    }

    getMediaTarget() {
      if (this.mediaTarget && this.mediaTarget.isConnected) return this.mediaTarget;

      var scope = this.closest('.shopify-section') || document;
      var candidates = [];

      for (var i = 0; i < MEDIA_SELECTORS.length; i++) {
        var found = scope.querySelectorAll(MEDIA_SELECTORS[i]);
        for (var j = 0; j < found.length; j++) {
          if (!found[j].contains(this) && candidates.indexOf(found[j]) === -1) {
            candidates.push(found[j]);
          }
        }
      }

      if (!candidates.length) return null;

      /* Prefer a column that is actually rendered at this breakpoint —
         "Media position: Right" outputs a mobile-only and a desktop-only one. */
      for (var k = 0; k < candidates.length; k++) {
        if (isVisible(candidates[k])) {
          this.mediaTarget = candidates[k];
          return candidates[k];
        }
      }

      this.mediaTarget = candidates[0];
      return candidates[0];
    }

    place() {
      if (this.placement === 'in_info') return;
      if (this.mql.matches) this.moveToDesktopPosition();
      else this.moveBackToInfo();
    }

    moveToDesktopPosition() {
      if (this.placement === 'below_product') {
        var product = this.productEl;
        if (!product || !product.parentNode) return;

        /* Keep multiple blocks in their authored order. */
        var ref = product;
        var next = ref.nextElementSibling;
        while (
          next &&
          next !== this &&
          next.tagName === 'PRODUCT-FLEX-BLOCK' &&
          next.getAttribute('data-desktop-placement') === 'below_product'
        ) {
          ref = next;
          next = ref.nextElementSibling;
        }

        if (this.parentNode !== ref.parentNode || this.previousElementSibling !== ref) {
          ref.parentNode.insertBefore(this, ref.nextSibling);
        }

        this.moved = true;
        this.setAttribute('data-placed', 'below_product');
        return;
      }

      var target = this.getMediaTarget();
      if (!target) return;
      if (this.parentNode !== target) target.appendChild(this);

      this.moved = true;
      this.setAttribute('data-placed', 'under_media');
    }

    moveBackToInfo() {
      if (this.anchor && this.anchor.parentNode && this.previousSibling !== this.anchor) {
        this.anchor.parentNode.insertBefore(this, this.anchor.nextSibling);
      }
      this.moved = false;
      this.mediaTarget = null;
      this.setAttribute('data-placed', 'in_info');
    }
  }

  customElements.define('product-flex-block', ProductFlexBlock);

  function replaceAll(root) {
    var blocks = (root || document).querySelectorAll('product-flex-block');
    requestAnimationFrame(function () {
      for (var i = 0; i < blocks.length; i++) {
        if (typeof blocks[i].place === 'function') blocks[i].place();
      }
    });
  }

  /* The gallery can finish building after the block upgrades — re-check. */
  window.addEventListener('load', function () {
    replaceAll(document);
  });

  /* Theme editor support. */
  document.addEventListener('shopify:section:load', function (event) {
    replaceAll(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    if (event.target && event.target.tagName === 'PRODUCT-FLEX-BLOCK') {
      event.target.place();
    }
  });
})();
